import { useState, useEffect, useCallback } from "react";
import { Dialog } from "primereact/dialog";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { getPrecios, agregarProducto } from "../../../service/AffariService";
import { InputText } from "primereact/inputtext";
import StockDialog from "./TableProductosStocks";
import { useGlobalContext } from "../../../Config";
import { useOffline } from "../../../offline/OfflineContext.js";
import { agregarProductoCotizacionOffline } from "../../../offline/offlineCotizacionService.js";
import {
  buscarProductosOffline,
  obtenerPrecioOffline,
  obtenerStockTotalOffline,
} from "../../../offline/offlineCatalogoService.js";

const TableProductos = ({
  visible,
  onCancel,
  idCotizacion,
  interfaz,
  data,
  onAdded,
  onNotify,
  loading,
  setLoading,
}) => {
  const { isDinar } = useGlobalContext();
  const { offlineMode } = useOffline();
  const [products, setProducts] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [isStockDialogVisible, setIsStockDialogVisible] = useState(false);
  const [idProducto, setIdProducto] = useState(null);
  const [lazyParams, setLazyParams] = useState({
    first: 0,
    rows: 50,
    page: 0,
    sortField: null,
    sortOrder: null,
  });

  const [searchTerm, setSearchTerm] = useState("");

  const loadData = useCallback(async () => {
    setLoading(true);

    try {
      if (offlineMode) {
        const limit = lazyParams.rows;
        const start = (lazyParams.page || 0) * lazyParams.rows;

        const todos = await buscarProductosOffline(searchTerm || "", 5000);

        const sortedData = sortData(
          todos,
          lazyParams.sortField,
          lazyParams.sortOrder,
        );

        const pageData = sortedData.slice(start, start + limit);

        const listaPrecio = data?.cliente?.listaPrecio?.codigo;
        const almacen =
          data?.almacen?.almacen || data?.cliente?.listaPrecio?.almacen;

        const pageDataConPrecioStock = await Promise.all(
          pageData.map(async (producto) => {
            const precioData = listaPrecio
              ? await obtenerPrecioOffline(listaPrecio, producto.codigo)
              : null;

            const stock = almacen
              ? await obtenerStockTotalOffline(almacen, producto.codigo)
              : 0;

            return {
              ...producto,
              precio: precioData?.precio ?? 0,
              stock,
            };
          }),
        );

        setProducts(pageDataConPrecioStock);
        setTotalRecords(sortedData.length);
        return;
      }

      const start = (lazyParams.page || 0) * lazyParams.rows;

      const response = await getPrecios({
        dialog: true,
        draw: 1,
        id: idCotizacion,
        length: lazyParams.rows,
        old: false,
        start,
        tipo: interfaz,
        search: searchTerm,
      });

      const resultData = response.data || [];
      const sortedData = sortData(
        resultData,
        lazyParams.sortField,
        lazyParams.sortOrder,
      );

      setProducts(sortedData);
      setTotalRecords(response.recordsFiltered ?? response.recordsTotal ?? 0);
    } catch (err) {
      console.error("Error fetching prices:", err);
    } finally {
      setLoading(false);
    }
  }, [
    idCotizacion,
    lazyParams.page,
    lazyParams.rows,
    lazyParams.sortField,
    lazyParams.sortOrder,
    searchTerm,
    setLoading,
    interfaz,
    offlineMode,
    data,
  ]);

  useEffect(() => {
    if (!visible) return;

    const delayDebounceFn = setTimeout(() => {
      loadData();
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [
    visible,
    lazyParams.first,
    lazyParams.rows,
    lazyParams.page,
    idCotizacion,
    interfaz,
    searchTerm,
    loadData,
    offlineMode,
  ]);

  const sortData = (data, field, order) => {
    if (!field || !data) return data;

    let sortedData = [...data];
    sortedData.sort((a, b) => {
      const value1 = a[field] ?? "";
      const value2 = b[field] ?? "";

      let comparison = 0;
      if (typeof value1 === "number" && typeof value2 === "number") {
        comparison = value1 - value2;
      } else {
        comparison = String(value1).localeCompare(String(value2), "es", {
          sensitivity: "base",
        });
      }

      return (order || 1) === 1 ? comparison : -comparison;
    });
    return sortedData;
  };

  const onPage = (event) => {
    setLazyParams((prev) => ({
      ...prev,
      first: event.first,
      rows: event.rows,
      page: event.page,
    }));
  };

  const onSort = (event) => {
    setLazyParams((prev) => ({
      ...prev,
      sortField: event.sortField,
      sortOrder: event.sortOrder,
    }));

    const sortedData = sortData(products, event.sortField, event.sortOrder);
    setProducts(sortedData);
  };

  const addProducto = async (codigo) => {
    try {
      setLoading(true);

      if (offlineMode) {
        await agregarProductoCotizacionOffline(idCotizacion, codigo);
      } else {
        await agregarProducto(idCotizacion, codigo);
      }

      if (typeof onNotify === "function") {
        onNotify("success", "Producto agregado", `Código ${codigo}`);
      }

      if (typeof onAdded === "function") onAdded(codigo);
      if (typeof onCancel === "function") onCancel();
    } catch (err) {
      console.error("Error agregando producto:", err);

      if (typeof onNotify === "function") {
        onNotify(
          "error",
          "Error al agregar el producto",
          err.message || "El producto ya fue agregado o no existe.",
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("es-CL", {
      style: "currency",
      currency: "CLP",
    }).format(value || 0);
  };

  return (
    <>
      <Dialog
        header="Listado de Productos"
        visible={visible}
        className="p-fluid custom-dialog"
        onHide={onCancel}
        style={{ width: "80vw" }}
      >
        <div>
          <div className="flex justify-content-end mb-3">
            <span className="p-input-icon-left w-full md:w-6">
              <i className="pi pi-search" />
              <InputText
                type="text"
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setLazyParams((prev) => ({ ...prev, first: 0, page: 0 }));
                }}
                placeholder="Buscar por código o descripción"
                className="w-full"
              />
            </span>
          </div>
          <DataTable
            value={products}
            lazy
            paginator
            first={lazyParams.first}
            rows={lazyParams.rows}
            totalRecords={totalRecords}
            onPage={onPage}
            onSort={onSort}
            sortField={lazyParams.sortField}
            sortOrder={lazyParams.sortOrder}
            loading={loading}
            rowsPerPageOptions={[20, 50, 100]}
            emptyMessage="No se encontraron productos"
            stripedRows
            showGridlines
            tableStyle={{ minWidth: "50rem" }}
            className="p-datatable-sm border-1 surface-border border-round"
            scrollHeight="55vh"
            scrollable
            selectionMode="single"
            onRowClick={(event) => addProducto(event.data.codigo)}
            rowClassName={() => "cursor-pointer"}
          >
            <Column
              field="codigo"
              header="Código"
              sortable
              body={(rowData) => (
                <span className="text-primary font-medium">
                  {rowData.codigo}
                </span>
              )}
            />
            <Column
              field="descripcion"
              header="Descripción"
              sortable
              body={(rowData) => rowData.descripcion}
            />
            <Column
              field="precio"
              header="Precio"
              body={(rowData) => formatCurrency(rowData.precio)}
              className="text-right"
              sortable
            />
            {!isDinar && interfaz === "MAYORISTA" && (
              <Column
                header="Detalle"
                body={(rowData) => (
                  <button
                    type="button"
                    className="p-button p-button-text p-button-sm"
                    title="Ver stock por centro"
                    onClick={(event) => {
                      event.stopPropagation();
                      setIdProducto(rowData.codigo);
                      setIsStockDialogVisible(true);
                    }}
                  >
                    <i className="pi pi-box" />
                  </button>
                )}
                style={{ width: "5rem" }}
              />
            )}
            {isDinar && (
              <Column
                field="stock"
                header="Stock"
                body={(rowData) => Math.round(Number(rowData.stock) || 0)}
                className="text-right"
                sortable
              />
            )}
          </DataTable>
        </div>
      </Dialog>

      <StockDialog
        visible={isStockDialogVisible}
        onCancel={() => {
          setIsStockDialogVisible(false);
        }}
        idProducto={idProducto}
        loading={loading}
        setLoading={setLoading}
      />
    </>
  );
};

export default TableProductos;
